import React, { useState } from 'react';

function EarningsAnalysisTable({ data, onAddToWatchlist, watchedStocks }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterTicker, setFilterTicker] = useState('all');

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value) * 100;
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    return <span className={color}>{num > 0 ? '+' : ''}{num.toFixed(2)}%</span>;
  };

  const formatEPS = (value) => {
    if (value == null || isNaN(value)) return '--';
    return Number(value).toFixed(2);
  };

  const getMoveColor = (value) => {
    if (value == null || isNaN(value)) return {};
    const num = Number(value) * 100;
    if (num > 5) return { backgroundColor: 'rgba(34, 197, 94, 0.3)' };
    if (num > 2) return { backgroundColor: 'rgba(34, 197, 94, 0.15)' };
    if (num > 0) return { backgroundColor: 'rgba(34, 197, 94, 0.05)' };
    if (num > -2) return { backgroundColor: 'rgba(239, 68, 68, 0.05)' };
    if (num > -5) return { backgroundColor: 'rgba(239, 68, 68, 0.15)' };
    return { backgroundColor: 'rgba(239, 68, 68, 0.3)' };
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const isWatched = (ticker) => {
    return watchedStocks?.some(stock => stock.ticker === ticker) || false;
  };

  const SortableHeader = ({ field, label }) => (
    <th
      className="text-right py-2 px-2 cursor-pointer hover:opacity-80 whitespace-nowrap"
      style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
      onClick={() => handleSort(field)}
    >
      {label}{' '}
      {sortField === field && (sortDirection === 'asc' ? '\u2191' : '\u2193')}
    </th>
  );

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No earnings data available</p>
        <p className="text-sm text-gray-500 mt-2">
          Run an analysis to see historical earnings price movements
        </p>
      </div>
    );
  }

  // Filter out error rows for display
  const validData = data.filter(row => !row.Error);
  const errorRows = data.filter(row => row.Error);

  const uniqueTickers = [...new Set(validData.map(row => row.Ticker))];

  let filteredData = validData;
  if (filterTicker !== 'all') {
    filteredData = filteredData.filter(row => row.Ticker === filterTicker);
  }

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const comparison = aVal > bVal ? 1 : -1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Filter by Ticker</label>
          <select
            value={filterTicker}
            onChange={(e) => setFilterTicker(e.target.value)}
            className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
          >
            <option value="all">All Tickers</option>
            {uniqueTickers.map(ticker => (
              <option key={ticker} value={ticker}>{ticker}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Messages */}
      {errorRows.length > 0 && (
        <div className="bg-red-900/20 border border-red-700 rounded-md p-3">
          <h4 className="text-red-400 text-sm font-medium mb-1">Errors:</h4>
          <ul className="text-red-300 text-xs space-y-1">
            {errorRows.map((row, i) => (
              <li key={i}>{row.Ticker}: {row.Error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-max">
          <thead>
            {/* Header Group Row */}
            <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
              <th className="text-left py-2 px-2 sticky left-0 z-10"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }} colSpan="2">
                EARNINGS EVENT
              </th>
              <th className="text-center py-2 px-2"
                style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }}
                colSpan="3">
                EPS DATA
              </th>
              <th className="text-center py-2 px-2"
                style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }}
                colSpan="5">
                PRICES
              </th>
              <th className="text-center py-2 px-2"
                style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }}
                colSpan="4">
                PRICE MOVES
              </th>
              <th className="text-center py-2 px-2 sticky right-0 z-10"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
              </th>
            </tr>
            {/* Column Headers Row */}
            <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
              <th className="text-left py-2 px-2 sticky left-0 z-10"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                Ticker
              </th>
              <SortableHeader field="Earnings Date" label="Date" />
              {/* EPS */}
              <SortableHeader field="EPS Actual" label="Actual" />
              <SortableHeader field="EPS Estimate" label="Estimate" />
              <SortableHeader field="EPS Surprise %" label="Surprise %" />
              {/* Prices */}
              <SortableHeader field="Price -10d" label="-10d" />
              <SortableHeader field="Price -1d" label="-1d" />
              <SortableHeader field="Price ED" label="ED Close" />
              <SortableHeader field="Price +1d" label="+1d" />
              <SortableHeader field="Price +10d" label="+10d" />
              {/* Moves */}
              <SortableHeader field="% Move Pre-Drift" label="Pre-Drift" />
              <SortableHeader field="% Move Overnight" label="Overnight" />
              <SortableHeader field="% Move Post-Drift" label="Post-Drift" />
              <SortableHeader field="% Move Total Window" label="Total" />
              {/* Actions */}
              <th className="text-center py-2 px-2 sticky right-0 z-10"
                style={{ backgroundColor: '#1a1a1a' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={index} className="border-b border-gray-800 hover:bg-gray-700/30">
                <td className="py-2 px-2 font-medium text-white sticky left-0 bg-gray-800">
                  {isWatched(row.Ticker) && <span className="mr-1">&#11088;</span>}
                  {row.Ticker}
                </td>
                <td className="py-2 px-2 text-right text-white">{row['Earnings Date']}</td>
                {/* EPS */}
                <td className="py-2 px-2 text-right text-white"
                  style={{ borderLeft: '1px solid #333' }}>
                  {formatEPS(row['EPS Actual'])}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {formatEPS(row['EPS Estimate'])}
                </td>
                <td className="py-2 px-2 text-right">
                  {formatPercent(row['EPS Surprise %'])}
                </td>
                {/* Prices */}
                <td className="py-2 px-2 text-right text-white"
                  style={{ borderLeft: '1px solid #333' }}>
                  {formatCurrency(row['Price -10d'])}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {formatCurrency(row['Price -1d'])}
                </td>
                <td className="py-2 px-2 text-right text-white font-medium">
                  {formatCurrency(row['Price ED'])}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {formatCurrency(row['Price +1d'])}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {formatCurrency(row['Price +10d'])}
                </td>
                {/* Moves with heatmap */}
                <td className="py-2 px-2 text-right"
                  style={{ ...getMoveColor(row['% Move Pre-Drift']),
                           borderLeft: '1px solid #333' }}>
                  {formatPercent(row['% Move Pre-Drift'])}
                </td>
                <td className="py-2 px-2 text-right"
                  style={getMoveColor(row['% Move Overnight'])}>
                  {formatPercent(row['% Move Overnight'])}
                </td>
                <td className="py-2 px-2 text-right"
                  style={getMoveColor(row['% Move Post-Drift'])}>
                  {formatPercent(row['% Move Post-Drift'])}
                </td>
                <td className="py-2 px-2 text-right font-medium"
                  style={getMoveColor(row['% Move Total Window'])}>
                  {formatPercent(row['% Move Total Window'])}
                </td>
                {/* Actions */}
                <td className="py-2 px-2 text-center sticky right-0 bg-gray-800">
                  {!isWatched(row.Ticker) && onAddToWatchlist && (
                    <button
                      onClick={() => onAddToWatchlist(row.Ticker)}
                      className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded border border-blue-400 hover:border-blue-300"
                    >
                      Watch
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="flex justify-between items-center text-sm text-gray-400">
        <div>
          Showing {sortedData.length} of {validData.length} earnings events
        </div>
      </div>

      {/* Interpretation Guide */}
      <div className="bg-gray-700/30 rounded-lg p-4 mt-4">
        <h4 className="text-sm font-medium text-gray-300 mb-2">Reading the Results</h4>
        <ul className="text-xs text-gray-400 space-y-1">
          <li>&#8226; <strong>Pre-Drift (-10d to ED)</strong>: Price movement in the ~10 trading days leading up to earnings</li>
          <li>&#8226; <strong>Overnight (-1d to +1d)</strong>: The earnings reaction / overnight gap move</li>
          <li>&#8226; <strong>Post-Drift (ED to +10d)</strong>: Price continuation or reversal after earnings</li>
          <li>&#8226; <strong>Total Window (-10d to +10d)</strong>: Full earnings window price change</li>
          <li>&#8226; <strong>EPS Surprise</strong>: Difference between actual and estimated EPS as a percentage</li>
        </ul>
      </div>
    </div>
  );
}

export default EarningsAnalysisTable;
